/**
 * El Programa de socios, del lado del cliente: la forma de lo que manda el
 * backend y las reglas que se pueden probar sin montar la página.
 *
 * El backend vuelve a validar TODO (`lib/partnerProgram.ts` en
 * job-apply-service); acá se valida para que el error se diga junto al campo y
 * antes del viaje, no para decidir. Si las dos listas de países divergen, la
 * del backend manda y el formulario muestra un error de país.
 */

/**
 * Los países en los que Stripe le puede pagar a un socio desde la cuenta de
 * TBit. Lista CERRADA: el país de la cuenta de cobro no se puede cambiar
 * después.
 *
 * Es COPIA de `PARTNER_CONNECT_COUNTRIES` (`lib/partnerProgram.ts` de
 * job-apply-service), que es la que manda: sale de `GET /v1/country_specs/US`
 * de Stripe (`supported_transfer_countries`, verificada el 2026-10-06), y ahí
 * está cómo re-verificarla. El formulario de `/socios` usa la que manda el
 * servidor en `GET /job-apply/api/partner` (`partnerCountries`); esta copia
 * existe como respaldo si el servidor no la manda. La landing pública (en
 * tbit.app, ver `PARTNER_LANDING_URL`) escribe la misma lista en su copy. Los
 * tests de los dos repos fijan la lista literal: cambiar una es cambiar la otra
 * (y el copy de la landing, en el repo website).
 */
export const PARTNER_COUNTRIES = [
  'US',
  'CA',
  'ES',
  'AR',
  'BO',
  'CL',
  'CO',
  'CR',
  'DO',
  'EC',
  'GT',
  'MX',
  'PA',
  'PE',
  'PY',
  'SV',
  'UY',
] as const;

/**
 * Los de la región donde Stripe NO transfiere desde una plataforma US, y que
 * más nos preguntan. Se nombran en voz alta (formulario y landing) para que
 * quien vive ahí no se quede buscando su país en la lista.
 */
export const PARTNER_UNSUPPORTED_COUNTRIES = ['HN', 'VE', 'NI', 'BR'] as const;

/**
 * La lista con la que se arma el selector: la del servidor si vino bien
 * formada, la copia local si no (un backend viejo, o una respuesta rara).
 */
export function partnerCountries(response: Pick<PartnerResponse, 'countries'> | null | undefined): string[] {
  const fromServer = response?.countries;
  if (Array.isArray(fromServer)) {
    const clean = fromServer.filter((c): c is string => typeof c === 'string' && /^[A-Z]{2}$/.test(c));
    if (clean.length) return clean;
  }
  return [...PARTNER_COUNTRIES];
}

/**
 * Los códigos en el orden alfabético de su nombre en el idioma de la pantalla:
 * con 17 países, buscar "Perú" no puede depender del orden en que Stripe los
 * fue habilitando.
 */
export function sortCountriesByName(codes: readonly string[], name: (code: string) => string, locale: string): string[] {
  return [...codes].sort((a, b) => name(a).localeCompare(name(b), locale));
}

/**
 * La landing pública del Programa de socios. Vivía aquí
 * (`/programa-de-socios`, `/partner-program`) y se mudó a tbit.app el
 * 2026-10-07, porque las landings públicas viven en el sitio (repo website,
 * `site/pages/job-apply-socios.ts`). Solo existe en español; nginx redirige con
 * 301 las dos rutas viejas a esta. Es la que enlazan la portada y la landing
 * de agencias, en los dos idiomas.
 */
export const PARTNER_LANDING_URL = 'https://tbit.app/es/job-apply/socios';

export type PartnerCountry = (typeof PARTNER_COUNTRIES)[number];

/**
 * Letras y dígitos, de 3 a 24. Más angosto que el `?ref=` (que acepta `-` y
 * `_`) porque el código también es un cupón de Stripe, y Stripe no acepta esos
 * dos caracteres en un cupón.
 */
const CODE_SHAPE = /^[A-Z0-9]{3,24}$/;

/** Lo que se escribe en el campo, ya en mayúsculas y sin espacios ni símbolos. */
export function cleanPartnerCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24);
}

export function isValidPartnerCode(code: string): boolean {
  return CODE_SHAPE.test(code);
}

export interface PartnerForm {
  name: string;
  whatsapp: string;
  country: string;
  community_name: string;
  promotion_plan: string;
  code: string;
}

export type PartnerField = keyof PartnerForm;

/**
 * Los campos que no pasan, en el orden del formulario. Vacío = se puede enviar.
 * `countries` es la lista que mandó el servidor (`partnerCountries`).
 */
export function invalidPartnerFields(
  form: PartnerForm,
  countries: readonly string[] = PARTNER_COUNTRIES,
): PartnerField[] {
  const bad: PartnerField[] = [];
  if (!form.name.trim()) bad.push('name');
  const digits = form.whatsapp.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) bad.push('whatsapp');
  if (!countries.includes(form.country)) bad.push('country');
  if (form.promotion_plan.trim().length < 10) bad.push('promotion_plan');
  if (!isValidPartnerCode(form.code)) bad.push('code');
  return bad;
}

/** "USD 5", "USD 12.50". Los montos del programa llegan en centavos de USD. */
export function formatUsdCents(cents: number): string {
  const units = (Number(cents) || 0) / 100;
  return `USD ${Number.isInteger(units) ? units : units.toFixed(2)}`;
}

// ─── Lo que manda el backend ────────────────────────────────────────────────

/**
 * TERMINATED (2026-10-08): el operador terminó la participación. Igual que
 * PAUSED, ve sus cobros y comisiones (lo causado se le paga) pero no la lista
 * de referidos, y su código ya no existe.
 */
export type PartnerStatus = 'PENDING' | 'ACTIVE' | 'PAUSED' | 'REJECTED' | 'TERMINATED';

/** Los estados con panel: tienen (o tuvieron) código y pueden tener comisiones por cobrar. */
export const PARTNER_PANEL_STATUSES: readonly PartnerStatus[] = ['ACTIVE', 'PAUSED', 'TERMINATED'];

export function hasPartnerPanel(status: PartnerStatus | null | undefined): boolean {
  return Boolean(status && PARTNER_PANEL_STATUSES.includes(status));
}
export type PartnerPayouts = 'none' | 'pending' | 'ready';
/**
 * En qué va cada referido. La regla es del backend (`referralStatus` en
 * `lib/partnerProgram.ts` de job-apply-service); acá sólo se pinta:
 *
 *  - `paying`   «Pagando»: plan vigente con al menos un cobro real.
 *  - `trial`    «En prueba»: en los 7 días gratis (trae cuándo termina).
 *  - `no_trial` «Sin prueba activa»: nunca empezó la prueba, o terminó sin pago.
 *  - `canceled` «Canceló»: tuvo plan pagado y ya no.
 *  - `courtesy` «Cortesía»: plan regalado por TBit, sin cobro. Aparte porque
 *               no comisiona.
 */
export type ReferralState = 'paying' | 'trial' | 'no_trial' | 'canceled' | 'courtesy';

/** El orden de los filtros y del contador: de lo que ya da plata a lo que no. */
export const REFERRAL_STATES: readonly ReferralState[] = ['paying', 'trial', 'no_trial', 'canceled', 'courtesy'];
/** VOID: el pago del referido se reembolsó o se disputó, y esa factura no paga. */
export type CommissionStatus = 'PENDING' | 'PAID' | 'FAILED' | 'VOID';

export interface PartnerView {
  id: string;
  status: PartnerStatus;
  code: string;
  name: string;
  email: string;
  whatsapp: string;
  country: string;
  community_name: string | null;
  promotion_plan: string;
  rejection_reason: string | null;
  /** Sólo con el socio aprobado: antes el código no existe en Stripe. */
  link: string | null;
  payouts: PartnerPayouts;
  /** Retención de pagos (sección 7 de los términos). Ausente en un backend anterior al 2026-10-08. */
  payouts_held?: boolean;
  /** Sólo con TERMINATED. */
  terminated_at?: string | null;
  created_at: string | null;
  approved_at: string | null;
  /**
   * Los Términos del Programa: la versión vigente, la última que aceptó y la
   * que lo rige. Ausente en un backend anterior al 2026-10-08.
   */
  terms?: PartnerTermsStatus;
}

export interface PartnerTermsStatus {
  doc: 'partner';
  version: string;
  accepted_version: string | null;
  accepted: boolean;
  governing_version: string | null;
}

/**
 * ¿Hay que pedirle que acepte la versión vigente? Sólo a un socio con panel
 * (aprobado) cuyo backend ya dice algo de los términos y que no la aceptó.
 */
export function needsPartnerTermsAcceptance(partner: PartnerView | null | undefined): boolean {
  return Boolean(partner && hasPartnerPanel(partner.status) && partner.terms && !partner.terms.accepted);
}

export interface PartnerReferral {
  name: string | null;
  /**
   * Completo: el socio acompaña a sus referidos y necesita poder escribirles.
   * Quien se registra con un código lo sabe (aviso de consentimiento del alta).
   */
  email: string;
  state: ReferralState;
  /** Sólo con `trial`: cuándo termina la prueba. */
  trial_ends_at?: string | null;
  /** Cuándo se registró en TBit Jobs. */
  registered_at?: string | null;
  /** Cuándo quedó con el código del socio. */
  referred_at: string | null;
  /**
   * La versión de los Términos del Programa con la que entró: sus comisiones
   * siguen esas reglas aunque los términos cambien. Ausente en un backend viejo.
   */
  terms_version?: string | null;
}

export type ReferralSummary = { total: number } & Record<ReferralState, number>;

/** Lo que se elige arriba de la lista: todos, o un estado. */
export type ReferralFilter = 'all' | ReferralState;

/**
 * El contador de arriba. El del servidor si vino (cuenta sobre toda la lista
 * que leyó); si no, se cuenta sobre lo que llegó. Un estado que el cliente no
 * conoce (un backend más nuevo) no rompe: suma al total y a nada más.
 */
export function summarizeReferrals(
  referrals: readonly PartnerReferral[],
  fromServer?: Partial<ReferralSummary> | null,
): ReferralSummary {
  const summary: ReferralSummary = { total: 0, paying: 0, trial: 0, no_trial: 0, canceled: 0, courtesy: 0 };
  if (fromServer && typeof fromServer.total === 'number') {
    summary.total = fromServer.total;
    for (const state of REFERRAL_STATES) summary[state] = Number(fromServer[state]) || 0;
    return summary;
  }
  for (const r of referrals) {
    summary.total++;
    if (REFERRAL_STATES.includes(r.state)) summary[r.state]++;
  }
  return summary;
}

function timeOf(value: string | null | undefined): number {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Los referidos que se ven con el filtro elegido, el más reciente arriba (por
 * fecha de registro; sin ella, por la de llegada con el código). El servidor
 * ya los manda en ese orden; se vuelve a ordenar para no depender de eso.
 */
export function visibleReferrals(referrals: readonly PartnerReferral[], filter: ReferralFilter): PartnerReferral[] {
  return referrals
    .filter((r) => filter === 'all' || r.state === filter)
    .sort((a, b) => timeOf(b.registered_at ?? b.referred_at) - timeOf(a.registered_at ?? a.referred_at));
}

export interface PartnerCommission {
  status: CommissionStatus;
  /** Sólo con VOID: `refunded` o `disputed`. */
  void_reason?: 'refunded' | 'disputed' | string | null;
  amount_cents: number;
  currency: string;
  date: string | null;
}

export interface PartnerTotals {
  pending_cents: number;
  paid_cents: number;
  failed_cents: number;
}

export interface PartnerResponse {
  partner: PartnerView | null;
  prefill: { name: string; email: string };
  /** La lista cerrada de países del servidor. Ausente en un backend anterior al 2026-10-06. */
  countries?: string[];
  /**
   * Sólo para el socio ACTIVO. Ausente con el socio en pausa: ahí el panel
   * sigue mostrando cobros y comisiones, pero no los datos de las personas.
   */
  referrals?: PartnerReferral[];
  referral_summary?: ReferralSummary;
  commissions?: PartnerCommission[];
  totals?: PartnerTotals;
}

/** El formulario vacío, o el de una solicitud rechazada para reenviarla. */
export function initialForm(response: PartnerResponse | null): PartnerForm {
  const p = response?.partner;
  return {
    name: p?.name ?? response?.prefill.name ?? '',
    whatsapp: p?.whatsapp ?? '',
    country: p?.country ?? '',
    community_name: p?.community_name ?? '',
    promotion_plan: p?.promotion_plan ?? '',
    code: p?.code ?? '',
  };
}
